/**
 * Open camera-/regierol op een draaidag + wie er die dag(en) vrij is (Joris, 13 okt 2026):
 * "Kan je bij mouse-over op een draaidag aangeven welke cameramensen of regisseurs wel beschikbaar
 * zijn op die draaidag, als er nog geen cameraman of regisseur is ingevuld?"
 *
 * - openRollen(): bezet = gekoppelde crew óf een naam in de titel met die functie. "Camera" of
 *   "Regie" als naam in de titel is een open plek, geen persoon.
 * - beoordeel(): de feiten per dag komen in één batch per week uit de engine
 *   (GET /Api/draaidagen-overzicht/beschikbaarheid); hier tellen we ze op voor de dagen van déze
 *   draaidag en laten we de draaidag zelf buiten beschouwing.
 *
 * Puur (geen React), zodat het los te testen is.
 */
import { crewUitTitel } from './draaidagCrew';

/** Zelfde woorden als de engine (services/draaidag_beschikbaarheid.ROL_SYNONIEMEN). */
export const ROL_SYNONIEMEN = {
  camera: ['camera', 'cameraman', 'camera operator', 'cinematograaf', 'dop'],
  regie: ['regie', 'regisseur', 'director'],
};
export const OPEN_ROLLEN = ['camera', 'regie'];
export const ROL_LABEL = { camera: 'Camera', regie: 'Regie' };
/** Zelfde drempel als de week-check: ≥ 4 uur afspraken op een dag = geen ruimte. */
export const AGENDA_VOL_UREN = 4;
/** Maximaal zoveel namen per groep in de tooltip; de rest als "+N meer". */
export const MAX_NAMEN = 8;

const ALLE_ROLWOORDEN = ['camera', 'cameraman', 'camera operator', 'regie', 'regisseur', 'director',
  'geluid', 'licht', 'grip', 'editor', 'crew', 'team'];

const norm = (s) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();
const voornaam = (s) => norm(s).split(' ')[0] || '';

/** multipleSelect: array, JSON-string of komma-string (CLAUDE.md, recurring bug). */
export const functieTekst = (raw) => {
  if (Array.isArray(raw)) return raw.join(', ');
  const s = String(raw || '').trim();
  if (s.startsWith('[')) {
    try { const w = JSON.parse(s); if (Array.isArray(w)) return w.join(', '); } catch { /* geen JSON */ }
  }
  return s;
};

export const rolPast = (rol, functie) => {
  // "Art Director" is geen regisseur (prod, week 44).
  const f = functieTekst(functie).toLowerCase().replace(/art director/g, '');
  return (ROL_SYNONIEMEN[rol] || [rol]).some((w) => f.includes(w));
};

export const isRolwoord = (naam) => {
  const n = norm(naam);
  return ALLE_ROLWOORDEN.some((w) => n === w || n.startsWith(`${w} `));
};

/** Locatiebezoek/recce heeft geen camera of regie nodig. */
const GEEN_CREW_NODIG = /^\s*(locatiebezoek|recce)\b/i;

/** Actieve personen (alle types) met functie, uit /Api/personen/all. */
export function personenMetFunctie(lijst) {
  return (Array.isArray(lijst) ? lijst : [])
    .map((x) => {
      const f = x.fields || {};
      const type = f.Type || x.type || x.person_type;
      return {
        naam: f['Volledige naam'] || x.full_name || x.naam || x.name || '',
        functie: functieTekst(f.Functie ?? x.functie ?? x.function).toLowerCase(),
        type: Array.isArray(type) ? type[0] : type,
        status: f.Status || x.status,
      };
    })
    .filter((p) => p.naam && (!p.status || p.status === 'Active'));
}

/** Personen die bij een crew-naam horen: volledige naam, anders een losse voornaam. */
function personenBij(crewNaam, personen) {
  const n = norm(String(crewNaam || '').replace(/\(.*?\)/g, '').replace(/\?/g, ''));
  if (!n) return [];
  const exact = personen.filter((p) => norm(p.naam) === n);
  if (exact.length || n.includes(' ')) return exact;
  return personen.filter((p) => voornaam(p.naam) === n);
}

/**
 * Welke van camera/regie is nog niet bezet?
 * @returns {{ open: string[], onbekend: string[] }}  onbekend = titelnamen die we niet in Personen vinden
 */
export function openRollen(rij, personen, rollen = OPEN_ROLLEN) {
  if (!rij || GEEN_CREW_NODIG.test(String(rij.origineleTitel || rij.titel || ''))) return { open: [], onbekend: [] };
  const gekoppeld = (rij.crew || []).filter((c) => typeof c === 'string' && c.trim() && !c.startsWith('rec'));
  // Een titelnaam valt alleen weg als hij dezelfde persoon is als iemand die al gekoppeld is:
  // losse voornaam = die voornaam, volledige naam = precies die naam. "Robin Smit" in de
  // titel naast gekoppelde "Robin Jansen" is iemand anders (Codex r1).
  const zelfdeAlsGekoppeld = (n) => (norm(n).includes(' ')
    ? gekoppeld.some((g) => norm(g) === norm(n))
    : gekoppeld.some((g) => voornaam(g) === norm(n)));
  const uitTitel = crewUitTitel(rij.origineleTitel || rij.titel)
    .map((x) => x.naam)
    .filter((n) => !isRolwoord(n) && !zelfdeAlsGekoppeld(n));
  const namen = [...gekoppeld, ...uitTitel];
  const onbekend = [];
  const bezet = new Set();
  namen.forEach((naam) => {
    const ps = personenBij(naam, personen || []);
    if (!ps.length) { onbekend.push(naam); return; }
    // Meerduidige voornaam (Robin cameraman, Robin regisseur): een rol telt alleen als bezet
    // als ze állemaal die rol hebben; anders weten we het niet en noemen we hem (Codex r1).
    if (ps.length > 1 && rollen.some((rol) => !ps.every((p) => rolPast(rol, p.functie)) && ps.some((p) => rolPast(rol, p.functie)))) {
      onbekend.push(`${naam} (meerdere)`);
    }
    rollen.forEach((rol) => { if (ps.every((p) => rolPast(rol, p.functie))) bezet.add(rol); });
  });
  return { open: rollen.filter((r) => !bezet.has(r)), onbekend };
}

const dagNa = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/** Maandag (YYYY-MM-DD) van de week waarin `iso` valt. */
export const maandagVan = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return dagNa(iso, -((d.getDay() + 6) % 7));
};
/** Weken (maandagen) die een draaidag raakt — een meerdaagse shoot kan over het weekend lopen. */
export const wekenVan = (datums) => [...new Set((datums || []).map(maandagVan))];

const DAG_KORT = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const dagKort = (iso) => DAG_KORT[new Date(`${iso}T12:00:00`).getDay()];

/**
 * Eén kandidaat over de dagen van deze draaidag.
 * @returns {{ beschikbaar: boolean, status: string, uren: number }}
 */
export function beoordeel(kandidaat, datums, eigenId, { freelance = false, drempel = AGENDA_VOL_UREN } = {}) {
  const meerdaags = datums.length > 1;
  const metDag = (iso, tekst) => (meerdaags ? `${tekst} (${dagKort(iso)})` : tekst);
  const andere = [];
  const vrij = [];
  const vol = [];
  let maxUren = 0;
  let alHier = false;
  let onbekend = false;
  datums.forEach((iso) => {
    const info = kandidaat.dagen?.[iso];
    if (!info) return;
    // Leesstatus per dag: bij een draaidag over twee weken kan één week wel en één niet gelezen zijn.
    const gelezen = info.agenda_gelezen ?? kandidaat.agenda_gelezen;
    if (!gelezen) onbekend = true;
    (info.draaidagen || []).forEach((d) => {
      if (d.id === eigenId) alHier = true;
      else andere.push(metDag(iso, d.naam || 'draaidag'));
    });
    if (info.afwezig) vrij.push(metDag(iso, info.afwezig));
    const u = Number(info.agenda_uren) || 0;
    if (gelezen && u >= drempel) vol.push(metDag(iso, `${Math.round(u)}u afspraken`));
    maxUren = Math.max(maxUren, u);
  });
  if (alHier) return { beschikbaar: false, status: 'staat al op deze draaidag', uren: maxUren };
  if (andere.length) return { beschikbaar: false, status: `op andere draaidag: ${[...new Set(andere)].join(', ')}`, uren: maxUren };
  if (vrij.length) return { beschikbaar: false, status: `vrij/afwezig: ${[...new Set(vrij)].join(', ')}`, uren: maxUren };
  if (vol.length) return { beschikbaar: false, status: `volle agenda: ${vol.join(', ')}`, uren: maxUren };
  if (freelance || !kandidaat.email) return { beschikbaar: true, status: 'vrij', uren: 0 };
  if (onbekend) return { beschikbaar: true, status: 'vrij, agenda onbekend', uren: 0 };
  // Naar beneden op een half uur: 3,75 u mag niet als "4u" lezen (dat is al een volle agenda).
  const u = Math.floor(maxUren * 2) / 2;
  return { beschikbaar: true, status: u > 0 ? `vrij, agenda ${String(u).replace('.', ',')}u bezet` : 'vrij', uren: maxUren };
}

/** De eerste functie van een kandidaat die bij de rol hoort ("Cameraman", niet "Editor"). */
const rolFunctie = (rol, functie) => {
  const delen = functieTekst(functie).split(/\s*,\s*/).filter(Boolean);
  return delen.find((d) => rolPast(rol, d)) || delen[0] || '';
};

/** Kandidaten van één rol samenvoegen over (zelden) twee weken: de dagen per naam bijeen. */
function kandidatenUit(weken, rol, groep) {
  const perNaam = new Map();
  (weken || []).forEach((w) => {
    ((w?.rollen?.[rol] || {})[groep] || []).forEach((k) => {
      const bestaand = perNaam.get(k.naam);
      if (!bestaand) perNaam.set(k.naam, { ...k, dagen: { ...(k.dagen || {}) } });
      else {
        Object.assign(bestaand.dagen, k.dagen || {});
        bestaand.agenda_gelezen = bestaand.agenda_gelezen && k.agenda_gelezen;
      }
    });
  });
  return [...perNaam.values()];
}

/**
 * Inhoud van de tooltip voor één rol.
 * @param weken  engine-antwoorden van de week (of weken) die de draaidag raakt
 * @returns {{ beschikbaar: {naam, functie, status, freelance}[], nietBeschikbaar: {naam, functie, status}[] }}
 */
export function tooltipVoorRol(weken, rol, rij, datums) {
  const beschikbaar = [];
  const nietBeschikbaar = [];
  [['vast', false], ['freelance', true]].forEach(([groep, freelance]) => {
    const rijen = kandidatenUit(weken, rol, groep).map((k) => {
      const b = beoordeel(k, datums, rij.id, { freelance });
      return { naam: k.naam, functie: rolFunctie(rol, k.functie), status: b.status, freelance,
        uren: b.uren, ingezet: Number(k.ingezet) || 0, ok: b.beschikbaar };
    });
    // Vast eerst, binnen een groep de rustigste agenda bovenaan; freelancers die we vaak
    // inzetten vóór de rest (anders 45 namen op alfabet).
    rijen.filter((x) => x.ok).sort((a, b) => a.uren - b.uren || b.ingezet - a.ingezet || a.naam.localeCompare(b.naam))
      .forEach(({ ok, uren, ingezet, ...x }) => beschikbaar.push(x));
    rijen.filter((x) => !x.ok).sort((a, b) => a.naam.localeCompare(b.naam))
      .forEach(({ ok, uren, ingezet, ...x }) => nietBeschikbaar.push(x));
  });
  return { beschikbaar, nietBeschikbaar };
}

/** Eerste MAX_NAMEN plus het aantal dat niet getoond wordt. */
export const beperkt = (lijst, max = MAX_NAMEN) => ({ zichtbaar: lijst.slice(0, max), meer: Math.max(0, lijst.length - max) });
